import React from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

interface Owner {
  id: string;
  name: string;
  type: string;
  address_line_1_owner?: string;
  city_owner?: string;
  state_owner?: string;
  zip_code_owner?: string;
  facilities_in_portfolio: number;
  role_suggestion: string;
  total_billed_in_portfolio: number;
  total_paid_in_portfolio: number;
  other_facilities: any[];
  other_portfolios: any[];
}

interface Facility {
  ccn: string;
  org_name: string;
  dba_name?: string;
  city: string;
  state: string;
  total_billed: number;
  total_paid: number;
  owners: string[];
}

interface OwnersTableProps {
  owners: Owner[];
  facilities: Facility[];
  expandedOwners: Set<string>;
  onOwnerExpand: (ownerId: string) => void;
  formatMoney: (amount: number) => string;
}

export const OwnersTable: React.FC<OwnersTableProps> = ({
  owners,
  facilities,
  expandedOwners,
  onOwnerExpand,
  formatMoney
}) => {
  return (
    <Box sx={{ px: 2 }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Owner Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>States</TableCell>
              <TableCell>Facilities</TableCell>
              <TableCell align="right">Total Billed</TableCell>
              <TableCell align="right">Total Paid</TableCell>
              <TableCell>Other Ownership Groups</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {owners?.map((owner: Owner) => (
              <React.Fragment key={owner.id}>
                <TableRow hover>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: owner.role_suggestion === 'LEAD' ? 0 : 3,
                        gap: 1
                      }}
                    >
                      {owner.role_suggestion === 'LEAD' && (
                        <Chip label="LEAD" size="small" color="primary" sx={{ fontWeight: 600 }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: owner.role_suggestion === 'LEAD' ? 600 : 500
                        }}
                      >
                        {owner.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={owner.type === 'O' ? 'Organization' : 'Individual'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {owner.address_line_1_owner ||
                    owner.city_owner ||
                    owner.state_owner ||
                    owner.zip_code_owner ? (
                      <Typography variant="caption">
                        {owner.address_line_1_owner && (
                          <>
                            {owner.address_line_1_owner}
                            <br />
                          </>
                        )}
                        {[owner.city_owner, owner.state_owner].filter(Boolean).join(', ')}
                        {owner.zip_code_owner && ` ${owner.zip_code_owner}`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {Array.from(
                        new Set(
                          facilities
                            ?.filter((facility: Facility) => facility.owners?.includes(owner.name))
                            ?.map((facility: Facility) => facility.state) || []
                        )
                      ).map((state: unknown) => (
                        <Chip
                          key={state as string}
                          label={state as string}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>{owner.facilities_in_portfolio}</TableCell>
                  <TableCell align="right">
                    {formatMoney(owner.total_billed_in_portfolio)}
                  </TableCell>
                  <TableCell align="right">
                    {formatMoney(owner.total_paid_in_portfolio)}
                  </TableCell>
                  <TableCell>
                    {owner.other_portfolios?.length > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`+${owner.other_portfolios.length}`}
                          size="small"
                          color="warning"
                        />
                        <IconButton size="small" onClick={() => onOwnerExpand(owner.id)}>
                          {expandedOwners.has(owner.id) ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>

                {/* Expandable row for other portfolios */}
                {owner.other_portfolios?.length > 0 && expandedOwners.has(owner.id) && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, borderBottom: 'none' }}>
                      <Collapse
                        in={expandedOwners.has(owner.id)}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ py: 2, pl: 4, backgroundColor: 'grey.50' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Other Ownership Groups ({owner.other_portfolios.length})
                          </Typography>
                          <List dense>
                            {owner.other_portfolios.map((portfolio: any, idx: number) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          mb: 0.5,
                                          textTransform: 'uppercase'
                                        }}
                                      >
                                        {portfolio.scheme_target_name || 'Unknown Portfolio'}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1 }}
                                      >
                                        {portfolio.count || 0} Facilities (
                                        {formatMoney(portfolio.total_billed || 0)})
                                      </Typography>
                                      <Box sx={{ pl: 1 }}>
                                        {portfolio.facility_details?.map(
                                          (facility: any, facilityIdx: number) => (
                                            <Typography
                                              key={facilityIdx}
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{ display: 'block', mb: 0.25 }}
                                            >
                                              - {facility.name} ({facility.state})
                                            </Typography>
                                          )
                                        ) ||
                                          // Fallback if facility_details not available
                                          portfolio.facility_names?.map(
                                            (name: string, nameIdx: number) => (
                                              <Typography
                                                key={nameIdx}
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', mb: 0.25 }}
                                              >
                                                - {name}
                                              </Typography>
                                            )
                                          )}
                                      </Box>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};